import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../Shared/AlertDialog/AlertDialog';
import { Button } from '../../Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../Shared/Dialog/Dialog';
import { Spinner } from '../../Shared/Spinner';
import { Switch } from '../../Shared/Switch/Switch';
import type { InboxAutoCloseSettingsDto } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { useModalStore } from '@/Domain/store/modal.store';

export const AUTO_CLOSE_SETTINGS_DIALOG = 'AUTO_CLOSE_SETTINGS_DIALOG';

type FormValues = InboxAutoCloseSettingsDto;

export const AutoCloseSettingsDialog = () => {
  const { t } = useTranslation('inbox_v2');
  const { isOpen, closeModal } = useModalStore();
  const open = isOpen(AUTO_CLOSE_SETTINGS_DIALOG);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const { data, isLoading } = useQuery<InboxAutoCloseSettingsDto>({
    queryKey: [QueryKey.inboxAutoCloseSettings.get],
    enabled: open,
  });

  const mutation = useMutation<
    InboxAutoCloseSettingsDto,
    Error,
    Partial<InboxAutoCloseSettingsDto>
  >({
    mutationKey: [QueryKey.inboxAutoCloseSettings.patch],
  });

  const form = useForm<FormValues>({
    defaultValues: { auto_close_whatsapp: true, auto_close_other_channels: true },
  });
  const isDirty = form.formState.isDirty;

  useEffect(() => {
    if (open && data) {
      form.reset({
        auto_close_whatsapp: data.auto_close_whatsapp,
        auto_close_other_channels: data.auto_close_other_channels,
      });
    }
  }, [open, data, form]);

  const handleSubmit = async (values: FormValues) => {
    try {
      await mutation.mutateAsync(values);
      form.reset(values);
      toast.success(t('auto_close.toast.save_success'));
      closeModal();
    } catch {
      toast.error(t('auto_close.toast.save_error'));
    }
  };

  const handleRequestClose = useCallback(() => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
    } else {
      closeModal();
    }
  }, [isDirty, closeModal]);

  const handleConfirmDiscard = () => {
    setConfirmDiscardOpen(false);
    closeModal();
  };

  const handleDialogOpenChange = (next: boolean) => {
    if (!next) handleRequestClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="w-lg" data-testid="auto-close-settings-dialog-content">
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <DialogHeader>
              <DialogTitle>
                <span className="flex items-center gap-2">
                  {t('auto_close.title')}
                  {isDirty && (
                    <span
                      className="rounded-full bg-warning-muted px-2 py-0.5 text-xs font-medium text-warning-foreground"
                      data-testid="auto-close-unsaved-badge"
                    >
                      {t('auto_close.unsaved.badge')}
                    </span>
                  )}
                </span>
              </DialogTitle>
              <DialogDescription>{t('auto_close.description')}</DialogDescription>
            </DialogHeader>

            {isLoading ? (
              <div className="flex justify-center py-6">
                <Spinner className="h-5 w-5" />
              </div>
            ) : (
              <div className="space-y-4">
                <Controller
                  control={form.control}
                  name="auto_close_whatsapp"
                  render={({ field }) => (
                    <div
                      className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-4"
                      data-testid="auto-close-whatsapp-row"
                    >
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-foreground">
                          {t('auto_close.whatsapp.label')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {t('auto_close.whatsapp.helper')}
                        </p>
                      </div>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="auto-close-whatsapp-switch"
                      />
                    </div>
                  )}
                />
                <Controller
                  control={form.control}
                  name="auto_close_other_channels"
                  render={({ field }) => (
                    <div
                      className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-4"
                      data-testid="auto-close-other-row"
                    >
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-foreground">
                          {t('auto_close.other.label')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {t('auto_close.other.helper')}
                        </p>
                      </div>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="auto-close-other-switch"
                      />
                    </div>
                  )}
                />
              </div>
            )}

            <DialogFooter>
              <Button variant="secondary" type="button" onClick={handleRequestClose}>
                {t('auto_close.cancel')}
              </Button>
              <Button
                variant="dark"
                type="submit"
                disabled={isLoading || mutation.isPending || !isDirty}
                data-testid="auto-close-settings-submit-button"
              >
                {t('auto_close.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDiscardOpen} onOpenChange={setConfirmDiscardOpen}>
        <AlertDialogContent data-testid="auto-close-discard-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('auto_close.unsaved.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('auto_close.unsaved.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="secondary" data-testid="auto-close-keep-editing-button">
                {t('auto_close.unsaved.keep_editing')}
              </Button>
            </AlertDialogCancel>
            <Button
              variant="danger"
              type="button"
              onClick={handleConfirmDiscard}
              data-testid="auto-close-discard-button"
            >
              {t('auto_close.unsaved.discard')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
